// Waterfalls (render-only): where an edge river crosses a tile boundary with
// a terrain-level drop of >= WATERFALL_MIN_DROP, a falling-water cascade is
// drawn on that edge. Pure derivation from snapshot props the client already
// receives (`riverEdges` 6-bit mask, `level` 0..=12) — no sim data added.
//
// Rivers flow downhill, so a qualifying edge is emitted once, from the HIGHER
// tile (the river's masks are set on both tiles of the edge).

import type { SceneNode } from '@aod/protocol';
import { EDGE_NEIGHBOURS, edgeNormal } from './terrainEdges.js';

/** Minimum level drop (inclusive) that produces a fall. */
export const WATERFALL_MIN_DROP = 2;
/** Hard cap on instances (cheap by construction; rivers are 1-3 per map). */
export const WATERFALL_CAP = 48;
/** Sheet width in world units (a river channel, not the whole 1.0 edge). */
export const WATERFALL_WIDTH = 0.46;
/** Never draw a fall shorter than this, even if tile heights disagree. */
export const WATERFALL_MIN_HEIGHT = 0.16;
/** Sanity cap on fall height (full 0 -> 12 span is 1.2 world units). */
export const WATERFALL_MAX_HEIGHT = 1.4;
/** Distance from tile centre to an edge midpoint (hex apothem, size 1). */
export const EDGE_APOTHEM = Math.sqrt(3) / 2;

export interface Waterfall {
  /** World-space edge midpoint (tile-local layout; scaled by the board group). */
  readonly x: number;
  readonly z: number;
  /** Outward edge normal (x, z) — the sheet faces along it, toward the low tile. */
  readonly nx: number;
  readonly nz: number;
  /** Y rotation (radians) that turns a +Z-facing plane to face the normal. */
  readonly yaw: number;
  readonly topY: number;
  readonly bottomY: number;
  readonly width: number;
  /** Level drop (>= WATERFALL_MIN_DROP). */
  readonly drop: number;
}

/** True when a river edge with this level drop should be drawn as a fall. */
export function qualifiesForWaterfall(fromLevel: number, toLevel: number): boolean {
  return fromLevel - toLevel >= WATERFALL_MIN_DROP;
}

/**
 * Fall extent from the high tile's edge surface Y.
 *
 * Tiles are heighted by level (elevation.tileSurfaceY), so the real surface
 * difference IS the fall. Falls only exist on levelled boards; `drop *
 * levelStep` is the fallback if the surfaces disagree (non-positive gap).
 * Height is bounded to [WATERFALL_MIN_HEIGHT, WATERFALL_MAX_HEIGHT].
 */
export function fallBounds(
  highSurfaceY: number,
  lowSurfaceY: number,
  drop: number,
  levelStep: number,
): { topY: number; bottomY: number } {
  const gap = highSurfaceY - lowSurfaceY;
  const raw = gap > 0 ? gap : drop * levelStep;
  const height = Math.min(WATERFALL_MAX_HEIGHT, Math.max(WATERFALL_MIN_HEIGHT, raw));
  return { topY: highSurfaceY, bottomY: highSurfaceY - height };
}

interface TileInfo {
  readonly q: number;
  readonly r: number;
  readonly level: number;
  readonly rivers: number;
}

export interface WaterfallEnv {
  readonly levelStep: number;
  /** Plateau/water-plane Y of a tile (elevation.tileSurfaceY). */
  readonly surfaceY: (q: number, r: number) => number;
  /** World XZ of a tile centre. */
  readonly centre: (q: number, r: number) => readonly [number, number];
  readonly visible?: ReadonlySet<string> | null;
}

/** Derive waterfalls from tile nodes. Deterministic order (by tile, edge). */
export function deriveWaterfalls(
  tiles: ReadonlyArray<SceneNode>,
  env: WaterfallEnv,
  cap = WATERFALL_CAP,
): Waterfall[] {
  const byKey = new Map<string, TileInfo>();
  for (const t of tiles) {
    const p = (t.props ?? {}) as Record<string, unknown>;
    const q = p.q as number | undefined;
    const r = p.r as number | undefined;
    const level = p.level as number | undefined;
    if (q === undefined || r === undefined || level === undefined) continue;
    byKey.set(`${q},${r}`, { q, r, level, rivers: Number(p.riverEdges ?? 0) });
  }
  const out: Waterfall[] = [];
  if (cap <= 0) return out;
  for (const tile of byKey.values()) {
    if (tile.rivers === 0) continue;
    for (let i = 0; i < 6; i++) {
      if (!(tile.rivers & (1 << i))) continue;
      const [dq, dr] = EDGE_NEIGHBOURS[i]!;
      const nq = tile.q + dq;
      const nr = tile.r + dr;
      const other = byKey.get(`${nq},${nr}`);
      if (!other || !qualifiesForWaterfall(tile.level, other.level)) continue;
      const vis = env.visible;
      if (vis && !vis.has(`${tile.q},${tile.r}`) && !vis.has(`${nq},${nr}`)) continue;
      const drop = tile.level - other.level;
      const [cx, cz] = env.centre(tile.q, tile.r);
      const [nx, nz] = edgeNormal(i);
      const { topY, bottomY } = fallBounds(
        env.surfaceY(tile.q, tile.r),
        env.surfaceY(nq, nr),
        drop,
        env.levelStep,
      );
      out.push({
        x: cx + nx * EDGE_APOTHEM,
        z: cz + nz * EDGE_APOTHEM,
        nx,
        nz,
        yaw: Math.atan2(nx, nz),
        topY,
        bottomY,
        width: WATERFALL_WIDTH,
        drop,
      });
      if (out.length >= cap) return out;
    }
  }
  return out;
}
